import { formatMoney } from '@app/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import { orgHref } from '../lib/public-router'
import { returnedCheckout } from '../lib/returned-checkout'
import { useTRPC } from '../lib/trpc'
import {
  Button,
  Card,
  Disclosure,
  Figure,
  Notice,
  PaymentStep,
  ReturnedCheckoutNotice,
  useCheckoutConfirmation,
  useReturnedCheckout,
} from '../ui'
import { PublicNotFound } from '../ui/ErrorPage'
import { PublicShell } from '../ui/PublicShell'
import './gift.css'

/**
 * The page an office-given card link opens, for choosing the card automatic
 * payments are taken from. No login: the `?token=` is the whole authority.
 *
 * It states what is being agreed to — the arrangement, its figure as it stands
 * now, and the words the mandate records — and then takes the card in Stripe's
 * card fields on this page. Nothing here records the mandate; the webhook
 * does, once Stripe has the card. A used or expired link is a page that says
 * so, never an error.
 */
export function AutopayLinkPage({
  orgId,
  onLogin,
}: {
  orgId: string
  onLogin?: () => void
}) {
  const trpc = useTRPC()
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token') ?? ''
  /* A checkout named in the address is a claim until the server reads it:
     written there when the card completes in place, or by Stripe on the way
     back from a method that sent the member away. */
  const [checkoutId, showCheckout] = useReturnedCheckout()
  const confirmation = useCheckoutConfirmation({
    onComplete: (id) => showCheckout(id),
  })
  const outcome = useQuery({
    ...trpc.donation.checkoutOutcome.queryOptions({
      shortId: orgId,
      checkoutId: checkoutId ?? '',
    }),
    enabled: checkoutId !== null,
    retry: false,
  })
  /* Saved means Stripe kept the card, usually before the webhook has recorded
     the mandate it carries. */
  const returned = returnedCheckout({
    checkoutId,
    status: outcome.data?.status,
    isError: outcome.isError,
  })
  const chosenHere = returned === 'paid'

  const org = useQuery(trpc.donation.org.queryOptions({ shortId: orgId }))
  const link = useQuery({
    ...trpc.ledger.officeAutopayLink.queryOptions({ shortId: orgId, token }),
    enabled: token.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
  })
  const open = useMutation({
    ...trpc.ledger.officeAutopayLinkCheckout.mutationOptions(),
  })
  const card = open.data?.card ?? null

  if (org.isLoading || org.error || !org.data) {
    return (
      <PublicShell name={null} orgId={null} onLogin={onLogin}>
        {org.isLoading ? (
          <div className="gift">
            <Card className="gift__card">
              <Notice>Loading…</Notice>
            </Card>
          </div>
        ) : (
          <PublicNotFound />
        )}
      </PublicShell>
    )
  }

  const o = org.data
  const view = link.data && link.data.status !== 'invalid' ? link.data : null
  const status = !token || link.isError ? 'invalid' : link.data?.status
  const unit = view?.cadence === 'annual' ? 'year' : 'month'
  /* A membership's link also opens the membership itself, every step filled
     in from the record — the same page the member's own update link opens. */
  const editLink = view?.canEdit ? (
    <Button
      variant="secondary"
      render={
        <a
          href={`${orgHref(orgId, 'pay')}?token=${encodeURIComponent(token)}&edit=1`}
        />
      }
    >
      Edit
    </Button>
  ) : null
  const figure = view ? (
    <Figure
      amount={`${formatMoney(view.rateCents)} a ${unit}`}
      label={`For ${view.label}.`}
    />
  ) : null

  return (
    <PublicShell
      name={o.name}
      nameHe={o.nameHe}
      language={o.publicLanguage}
      orgId={o.shortId}
      acceptsDonations={o.acceptsDonations}
      contact={o}
      onLogin={onLogin}
    >
      <div className="gift">
        <Card className="gift__card">
          <h1 className="gift__title">Automatic payments</h1>
          {/* Whose payments these are, before anything asks for a card. An
              answer from a server that does not name them yet says nothing
              rather than half a sentence. */}
          {view?.personName ? (
            <p className="gift__who">
              For <bdi>{view.personName}</bdi>
            </p>
          ) : null}
          {token && link.isLoading ? (
            <p className="gift__muted">Loading…</p>
          ) : status === 'invalid' || !view ? (
            <Notice>
              This link is not valid. Please use the link the office gave you,
              or contact the office.
            </Notice>
          ) : status === 'completed' ? (
            <>
              <Notice tone="info">
                Automatic payments are set up: {formatMoney(view.rateCents)}{' '}
                each {unit} for {view.label}, until you or the office stop it.
              </Notice>
              {editLink}
              <a href={orgHref(o.shortId, '')}>Back to {o.name}</a>
            </>
          ) : returned === 'checking' ? (
            <p className="gift__muted">Checking the card…</p>
          ) : returned === 'error' || returned === 'processing' ? (
            <>
              {/* Whether the card was kept is not known yet, so nothing here
                  offers to save one again: the card is read again, or the
                  member goes back to the shul's page. */}
              <ReturnedCheckoutNotice
                state={returned}
                processing="Your card is being confirmed. Automatic payments start once it is, and there is nothing more to do."
              />
              {returned === 'error' ? (
                <Button
                  variant="secondary"
                  disabled={outcome.isFetching}
                  onClick={() => void outcome.refetch()}
                >
                  Check again
                </Button>
              ) : null}
              <a href={orgHref(o.shortId, '')}>Back to {o.name}</a>
            </>
          ) : chosenHere ? (
            <>
              {/* Saved before the card is on record: thanked for what they
                  did, without stating terms nothing yet confirms. */}
              <Notice tone="info">
                Thank you — your card has been received. Automatic payments
                start once it is recorded, which takes a moment.
              </Notice>
              <a href={orgHref(o.shortId, '')}>Back to {o.name}</a>
            </>
          ) : status === 'expired' ? (
            <Notice>
              This link has expired. Please ask the office for a new one.
            </Notice>
          ) : (
            <>
              {figure}
              <p className="gift__muted">
                Choose the card {view.orgName} charges for it.
              </p>
              {open.error ? (
                <Notice tone="error">{open.error.message}</Notice>
              ) : null}
              <ReturnedCheckoutNotice
                state={returned}
                unpaid="The card was not saved. Nothing has been set up; you can try again."
                expired="The card was not saved. Nothing has been set up; you can try again."
              />
              {open.data && !card ? (
                <Notice>
                  {open.data.reason ??
                    'This link can no longer be used. Please contact the office.'}
                </Notice>
              ) : null}
              {card ? (
                <PaymentStep
                  orgId={orgId}
                  card={card}
                  what={view.label}
                  who={view.personName}
                  amount={`${formatMoney(view.rateCents)} a ${unit}`}
                  amountNote="Nothing is charged today"
                  payLabel="Save card"
                  confirmation={confirmation}
                  onBack={() => {
                    confirmation.reset()
                    open.reset()
                    showCheckout(null)
                  }}
                />
              ) : (
                <Button
                  onClick={() => {
                    showCheckout(null)
                    open.mutate({ shortId: orgId, token })
                  }}
                  disabled={open.isPending}
                >
                  {open.isPending ? 'Opening secure payment…' : 'Add card'}
                </Button>
              )}
              {editLink}
              {/* The words the mandate records stay one press away before the
                  card is chosen, rather than a paragraph of small print on it. */}
              <Disclosure title="Terms and conditions" flush>
                <p className="gift__fine">{view.consentText}</p>
                <p className="gift__fine">
                  Choosing a card charges nothing by itself; each payment is
                  taken when it falls due.
                </p>
              </Disclosure>
            </>
          )}
        </Card>
      </div>
    </PublicShell>
  )
}
